---
title: Set up voice for a persona
summary: Choose a voice provider and voice, pick the speech-recognition language, and tune delivery.
visibility: authenticated
feature: voice
audience: author-admin
section: How-to
order: 60
last_verified: 2026-07-21
owner: docs
---

# Set up voice for a persona

A voice persona listens to spoken input and replies out loud. You configure it on the
**Presentation** tab of **Persona Settings**.

## 1. Switch to a voice mode

On the persona's card, open the **⋯** menu and choose **Settings…**. In **Persona
Settings**, go to **Presentation** and choose **Voice** (or **Avatar**, which also
speaks). The voice configuration section appears.

## 2. Pick a provider and voice

- **Provider** — the text-to-speech engine: **ElevenLabs**, **Azure Speech**, or
  **Kokoro (local)**.
- **Voice** — the specific voice to speak with, chosen from the provider's catalog. Use
  the voice picker to browse and preview.

Speech is synthesized in the browser using the configured API keys, so the provider you
pick must have a key available (see [Manage credentials](/help/how-to/manage-credentials)).

## 3. Choose the speech-recognition language

**Speech Recognition Language** controls how spoken input is transcribed. Leaving it on
the multilingual default lets the system auto-detect across several common languages;
picking a specific language transcribes only that one, which can be more accurate when you
know what to expect.

The language for a given conversation is resolved in order: a per-conversation override →
this persona's setting → the user's personal setting → multilingual auto-detect.

## 4. Tune delivery (ElevenLabs)

When the provider is ElevenLabs you can fine-tune the voice:

- **TTS Model** — the synthesis model (for example a low-latency flash/turbo model).
- **Stability** — steadier vs. more expressive delivery.
- **Similarity** — how closely the output tracks the source voice.
- **Pronunciation Rules** — up to three pronunciation dictionaries for names or jargon the
  voice otherwise mispronounces.

## 5. Save and try it

Click **Save Changes**, start a conversation, and use the microphone control to speak. The
persona transcribes what you said, answers from its knowledge base, and speaks the reply.

## See also

- [Persona configuration reference](/help/reference/persona-config) — every voice field.
- [Set up a video avatar](/help/how-to/avatar-setup) — for a face as well as a voice.
